import { Editor } from '@tiptap/react';
import * as React from 'react'
import _debounce from 'lodash.debounce'

export interface Context {
    globalEditor: [Editor | null, React.Dispatch<React.SetStateAction<Editor | null>>]
    registerDebouncedUpdate: (id: string, fn: ReturnType<typeof _debounce>) => void
    unregisterDebouncedUpdate: (id: string) => void
    flushAllPendingUpdates: () => Promise<void>
}

export const EditorCoordinatorContext = React.createContext<Context>({} as any);

export const EditorCoordinatorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [editor, setEditor] = React.useState<Editor | null>(null)
    const debouncedUpdatesRef = React.useRef<Map<string, ReturnType<typeof _debounce>>>(new Map())

    const registerDebouncedUpdate = React.useCallback((id: string, fn: ReturnType<typeof _debounce>) => {
        debouncedUpdatesRef.current.set(id, fn)
    }, [])

    const unregisterDebouncedUpdate = React.useCallback((id: string) => {
        debouncedUpdatesRef.current.delete(id)
    }, [])

    const flushAllPendingUpdates = React.useCallback(() => {
        console.log('[EditorCoordinator] Flushing all pending updates, count:', debouncedUpdatesRef.current.size)

        // Flush all debounced functions synchronously
        debouncedUpdatesRef.current.forEach((fn) => {
            fn.flush()
        })

        // Return a promise that resolves after a microtask
        // This ensures all synchronous state updates have been processed
        return new Promise<void>((resolve) => {
            // Use setTimeout(0) to wait for all queued state updates to complete
            setTimeout(() => {
                console.log('[EditorCoordinator] All pending updates flushed and processed')
                resolve()
            }, 0)
        })
    }, [])

    return (
        <EditorCoordinatorContext.Provider value={{
            globalEditor: [editor, setEditor],
            registerDebouncedUpdate,
            unregisterDebouncedUpdate,
            flushAllPendingUpdates
        }}>
            {children}
        </EditorCoordinatorContext.Provider>
    )
}

export const useEditorCoordinator = () => React.useContext(EditorCoordinatorContext);